import { createContext, useContext, useEffect, useState, type ReactNode, type JSX } from 'react';
import { type User, type Session } from '@supabase/supabase-js';
import type { Profile } from '../types';
import {
    getSessionFromDb,
    refreshSessionInDb,
    subscribeToAuthChanges,
    fetchProfileFromDb,
    signInWithPasswordInDb,
    signOutFromDb,
    startAuthAutoRefresh,
    stopAuthAutoRefresh,
} from '../services/authService';

// ── Context type ───────────────────────────────────────────────────────────────

interface AuthContextType {
    session: Session | null;
    user:    User | null;
    profile: Profile | null;
    loading: boolean;
    signIn:  (email: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ── Provider ───────────────────────────────────────────────────────────────────

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
    const [session, setSession] = useState<Session | null>(null);
    const [user,    setUser]    = useState<User | null>(null);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // ── Session init + auth state listener ────────────────────────────────────

    useEffect(() => {
        let mounted = true;

        // Load the existing session on mount so the app doesn't flash the
        // login screen on a hard refresh when the user is already signed in.
        const initSession = async () => {
            const session = await getSessionFromDb();
            if (!mounted) return;
            setSession(session);
            setUser(session?.user ?? null);
            if (session?.user) void fetchProfileFromDb(session.user.id).then(p => setProfile(p));
            setLoading(false);
        };

        void initSession();

        // Listen for auth state changes (sign in, sign out, token refresh).
        // Updates session, user, and profile state to stay in sync with Supabase.
        const unsubscribe = subscribeToAuthChanges((_event, session) => {
            setSession(session);
            setUser(session?.user ?? null);
            if (session?.user) void fetchProfileFromDb(session.user.id).then(p => setProfile(p));
            else setProfile(null);
        });

        return () => {
            mounted = false;
            unsubscribe();
        };
    }, []);

    // ── Session lifecycle (focus / visibility) ─────────────────────────────────

    useEffect(() => {
        // Checks whether a valid session exists and refreshes it if not.
        // Dispatches `app:resume` afterwards so other parts of the app
        // (e.g. data hooks) know they can safely re-fetch.
        const ensureFreshSession = async () => {
            try {
                const session = await getSessionFromDb();
                if (!session) await refreshSessionInDb();
            } catch (e) {
                console.warn('Error ensuring fresh session:', e);
            } finally {
                window.dispatchEvent(new CustomEvent('app:resume'));
            }
        };

        // When the window regains focus, restart the auto-refresh timer and
        // verify the session is still valid.
        const onFocus = () => {
            startAuthAutoRefresh();
            void ensureFreshSession();
        };

        // When the window loses focus, pause the auto-refresh timer to avoid
        // unnecessary background token refreshes.
        const onBlur = () => stopAuthAutoRefresh();

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') onFocus();
            else onBlur();
        };

        // Run once on mount to handle the case where the app loads in a
        // background tab or after a long period of inactivity.
        void ensureFreshSession();

        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            onBlur();
        };
    }, []);

    // ── Auth actions ───────────────────────────────────────────────────────────

    const signIn = (email: string, password: string): Promise<void> =>
        signInWithPasswordInDb(email, password);

    const signOut = async (): Promise<void> => {
        await signOutFromDb();
        setSession(null);
        setUser(null);
        setProfile(null);
    };

    return (
        <AuthContext.Provider value={{ session, user, profile, signIn, signOut, loading }}>
            {children}
        </AuthContext.Provider>
    );
}

// ── Hook ───────────────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}